/**
 * simulator/api/availabilityGate.js
 *
 * Decide si una llamada de disponibilidad (/available-days, /available-slots)
 * se resuelve con datos fake del simulador o pasa al backend real.
 *
 * El estado del simulador puede quedar MIXTO: al elegir "Peluquería unisex"
 * (peluqueria_unisex) SimulatorSectorSelect carga trabajadores REALES del
 * backend, pero ese sector no tiene `default_services` en c_sector_config, así
 * que los servicios caen al catálogo mock (SET_SECTOR resuelve ambos por
 * separado). Con servicio mock + trabajador real, decidir solo por el
 * trabajador mandaba `sim-pel-s9` al backend real → 404 "Service not found"
 * y la demo se quedaba sin días (alert + calendario vacío).
 *
 * El servicio manda: es lo que el backend busca en p_services.
 */

/** Trabajadores del catálogo mock: `sim-pel-w2`, `sim-lav-w2`, … */
export const isMockWorkerId = (id) =>
  typeof id === "string" && id.startsWith("sim-");

/**
 * Servicios del catálogo mock: `sim-pel-s9` y, en multiservicio, el id
 * sintético `multi_sim-pel-s2_sim-pel-s9` (que NO empieza por `sim-`).
 */
export const isMockServiceId = (id) => {
  if (typeof id !== "string" || !id) return false;
  if (id.startsWith("sim-")) return true;
  if (id.startsWith("multi_")) {
    return id
      .slice("multi_".length)
      .split("_")
      .some((part) => part.startsWith("sim-"));
  }
  return false;
};

/**
 * Servicios creados PARA EL LEAD (tabla c_lead_demo_carta, modo lead-appointment):
 * el Lambda les genera un UUID v4. Nunca existen en p_services (los reales de
 * conextcrm-e2f0b3 son slugs tipo `barberia_corte_caballero`), así que el
 * backend real siempre respondería 404. Cubre también el id sintético
 * `multi_<uuid>_<uuid>` (un UUID no lleva `_`, el split es seguro).
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isLeadCartaServiceId = (id) => {
  if (typeof id !== "string" || !id) return false;
  if (UUID_RE.test(id)) return true;
  if (id.startsWith("multi_")) {
    return id.slice("multi_".length).split("_").some((part) => UUID_RE.test(part));
  }
  return false;
};

/**
 * Devuelve true si la petición debe resolverse con datos fake.
 * - Sin worker_id ("sin preferencia") o worker mock → fake (comportamiento previo).
 * - Servicio del catálogo mock → fake aunque el trabajador sea real (el backend
 *   real no conoce ese servicio y respondería 404).
 * - Servicio por lead (UUID de lead-carta) → fake por la misma razón
 *   (incidente PERRO AZUL / canina, 3/9/2026).
 */
export const shouldFakeAvailability = (params = {}) => {
  const workerId = params?.worker_id;
  if (!workerId || isMockWorkerId(workerId)) return true;
  if (isMockServiceId(params?.service_id)) return true;
  if (isLeadCartaServiceId(params?.service_id)) return true;
  return false;
};
